import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
  RadioGroup,
  RadioGroupItem,
  Label,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import ModalMessage from '../../../UI/organisms/ModalMessage';
import { withTranslation, WithTranslation } from 'next-i18next';

interface PaymentOptionsModalOwnProps {
  hideModal: () => void;
  nextStep: (option: string | null) => void;
  message?: string;
  messageType?: string;
  hasActiveSubscription?: boolean;
}

type PaymentOptionsModalContentProps = PaymentOptionsModalOwnProps &
  WithTranslation;

const PaymentOptionsModalContent = (props: PaymentOptionsModalContentProps) => {
  const { nextStep, t, hideModal, message, messageType, hasActiveSubscription } =
    props;
  const [chosenOption, setChosenOption] = useState<string | null>(null);
  const [changeAdded, setChangeAdded] = useState(false);

  const StripeContent = (
    <div className="flex">
      <div className="block ml-2">
        <img
          src="/static/images/visa.png"
          alt="Visa logo"
          className="w-[3.34em] h-8 mr-2.5 mt-1 inline-block object-contain"
        />
        <img
          src="/static/images/master.png"
          alt="Mastercard logo"
          className="w-[3.34em] h-8 mr-2.5 mt-1 inline-block object-contain"
        />
        <img
          src="/static/images/amex.png"
          alt="Amex logo"
          className="w-[3.34em] h-8 mr-2.5 mt-1 inline-block object-contain"
        />
      </div>
    </div>
  );

  const PayPalContent = (
    <div className="flex">
      <div className="block ml-2">
        <img
          src="/static/images/paypal.png"
          alt="PayPal logo"
          className="w-36 h-8 mr-2.5 mt-1 inline-block object-contain"
        />
      </div>
    </div>
  );

  const goNext = () => {
    nextStep(chosenOption);
  };

  const handleUpdateOption = (value: string) => {
    setChosenOption(value);
    setChangeAdded(true);
  };

  return (
    <div className="max-w-lg mx-auto">
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>
            {t('student.billing.checkout.pickMethod.title')}
          </CardTitle>
          <button type="button" className="modal-close" onClick={hideModal}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <div>
            <p className="mb-8">
              {t('student.billing.checkout.pickMethod.intro')}
            </p>
            <RadioGroup
              value={chosenOption ?? undefined}
              onValueChange={handleUpdateOption}
              className="space-y-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Stripe" id="stripe" />
                <Label htmlFor="stripe" className="cursor-pointer">
                  {StripeContent}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="PayPal" id="paypal" />
                <Label htmlFor="paypal" className="cursor-pointer">
                  {PayPalContent}
                </Label>
              </div>
            </RadioGroup>
            {hasActiveSubscription && chosenOption === 'PayPal' && (
              <div className="paypal-warning mt-6">
                <p>
                  <b>
                    {t(
                      'student.billing.checkout.pickMethod.paypalWarning.title',
                    )}
                  </b>
                </p>
                <ul>
                  <li>
                    {t(
                      'student.billing.checkout.pickMethod.paypalWarning.transfer',
                    )}
                  </li>
                  <li>
                    {t(
                      'student.billing.checkout.pickMethod.paypalWarning.renew',
                    )}
                  </li>
                </ul>
              </div>
            )}
          </div>
          <Separator className="my-4" />
          <div className="control-group text-right">
            <Button className="mr-2" variant="destructive" onClick={hideModal}>
              {t('links.cancel')}
            </Button>
            <Button type="submit" onClick={goNext} disabled={!changeAdded}>
              {t('links.next')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(PaymentOptionsModalContent);

export default wrapped;
